import type { ComponentProps, ReactNode } from "react";
import { Spinner } from "@/components/ui/spinner";
import type { FieldError } from "@clerk/shared/types";
import { AlertCircle, PaletteIcon } from "lucide-react";
import Background from "@/components/ui/background";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AppearanceSettings from "@/components/nav_components/appearance-settings";
import { Button } from "@/components/ui/button";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import {
  OAUTH_PROVIDERS,
  PRIVACY_URL,
  TERMS_URL,
  type AuthOAuthStrategy,
} from "./auth-utils";

/**
 * Split page: the form on the left, unboxed, with the wordmark above and the
 * appearance menu below; on large screens the BrandPanel on the right.
 */
export function AuthShell({
  headline,
  children,
}: {
  headline: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative grid min-h-dvh w-full lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex min-h-dvh flex-col px-6 py-6 sm:px-10">
        <header>
          <img src="/logo_full_primary.svg" alt="Uplodio" className="-my-2 h-10 w-auto" />
        </header>
        <main className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-sm">{children}</div>
        </main>
        <footer className="flex items-center justify-end">
          <AppearanceMenu />
        </footer>
      </div>
      <BrandPanel headline={headline} />
      <Background />
    </div>
  );
}

/** The dashboard's "Edit Appearance" settings, behind one button. */
function AppearanceMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="muted-ghost"
          size="icon-md"
          aria-label="Appearance"
         
        >
          <PaletteIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="min-w-56">
        <AppearanceSettings />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Heading, form and the sign-in / sign-up switch line, left aligned. */
export function AuthContent({
  title,
  description,
  footer,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
      {footer && <p className="text-sm text-muted-foreground">{footer}</p>}
    </div>
  );
}

/** A spinner in place of the form, for redirects and invitation hand-offs. */
export function AuthLoading({ title, description }: { title: string; description?: string }) {
  return (
    <AuthContent title={title} description={description}>
      <Spinner className="size-5 text-muted-foreground" />
    </AuthContent>
  );
}

/**
 * The brand side, on the onboarding sidebar's surface with the onboarding
 * page's diagonal hatch over it: the page's headline in the serif, with a
 * plain line of small print (copyright, terms, privacy) at the foot.
 */
function BrandPanel({ headline }: { headline: ReactNode }) {
  return (
    <aside className="hidden p-4 lg:sticky lg:top-0 lg:block lg:h-dvh">
      <div className="relative flex h-full flex-col justify-between gap-10 overflow-hidden rounded-3xl border bg-accent-foreground/5 px-12 dark:bg-sidebar py-14 xl:px-16">
        {/* Same hatch as the onboarding page's content area. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(315deg,--theme(--color-muted-foreground/0.5)_0,--theme(--color-muted-foreground/0.5)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed opacity-30"
        />

        <h2 className="relative max-w-lg text-balance opacity-90 font-instrument-serif text-5xl leading-[1.05] xl:text-6xl">
          {headline}
        </h2>

        <div className="relative flex items-center justify-between gap-6 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Uplodio</span>
          <div className="flex items-center gap-4">
            <a href={TERMS_URL} target="_blank" rel="noreferrer" className="hover:text-foreground">
              Terms of Use
            </a>
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="hover:text-foreground">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
}

export function OAuthButtons({
  pending,
  disabled,
  onSelect,
}: {
  pending: AuthOAuthStrategy | null;
  disabled?: boolean;
  onSelect: (strategy: AuthOAuthStrategy) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {OAUTH_PROVIDERS.map((provider) => (
        <Button
          key={provider.strategy}
          type="button"
          variant="secondary"
          className="h-10 text-sm"
          disabled={disabled || pending !== null}
          onClick={() => onSelect(provider.strategy)}
        >
          {pending === provider.strategy ? (
            <Spinner />
          ) : (
            <img src={provider.icon} alt="" className="size-4" />
          )}
          {provider.label}
        </Button>
      ))}
    </div>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      or
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/** The first request-level error from a Clerk signal (errors.global). */
export function AuthErrorAlert({
  errors,
  message,
}: {
  errors?: { message: string; longMessage?: string }[] | null;
  message?: string | null;
}) {
  const first = errors?.[0];
  const text = message ?? first?.longMessage ?? first?.message;
  if (!text) return null;
  return (
    <Alert variant="destructive" className="rounded-lg">
      <AlertTitle>
        <AlertCircle className="size-4" />
        Something went wrong
      </AlertTitle>
      <AlertDescription>{text}</AlertDescription>
    </Alert>
  );
}

/** A Clerk field error under its input. */
export function FieldMessage({ error }: { error?: FieldError | null }) {
  if (!error) return null;
  return (
    <p className="text-xs text-destructive animate-in fade-in-0 duration-200">
      {error.longMessage ?? error.message}
    </p>
  );
}

/** Six-digit one-time code, as shadcn's InputOTP: digits only, 3 + 3 slots. */
export function CodeInput({
  id,
  value,
  onChange,
  onComplete,
  disabled,
  invalid,
  autoFocus,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Fires once all six digits are in (typed or pasted). */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const slot = (index: number) => (
    <InputOTPSlot
      key={index}
      index={index}
      aria-invalid={invalid || undefined}
      className="size-11 text-base font-medium"
    />
  );
  return (
    <InputOTP
      id={id}
      maxLength={6}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      value={value}
      disabled={disabled}
      onChange={onChange}
      onComplete={onComplete}
    >
      <InputOTPGroup>{[0, 1, 2].map(slot)}</InputOTPGroup>
      <InputOTPSeparator className="text-muted-foreground" />
      <InputOTPGroup>{[3, 4, 5].map(slot)}</InputOTPGroup>
    </InputOTP>
  );
}

export function TextButton({ className, ...props }: ComponentProps<"button">) {
  return (
    <button
      type="button"
      className={cn(
        "w-fit cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Where Clerk mounts its bot-protection widget (Turnstile) during sign-up.
 * It must be in the DOM whenever a sign-up request is sent.
 */
export function ClerkCaptcha() {
  const { theme } = useTheme();
  return (
    <div
      id="clerk-captcha"
      data-cl-theme={theme === "system" ? "auto" : theme}
      data-cl-size="flexible"
      className="empty:hidden"
    />
  );
}
